import Tag from "@/components/Tag";
import { TagType } from "@/components/Tag/Tag";
import type { MaterialNames } from "@/components/material_symbol_names";
import { PostStage } from "./types";
import Tooltip from "@/components/Tooltip";
import { toDateFormat } from "@/lib/dateHelpers";
import Icon from "@/components/Icon";
import { useLocation } from "react-router-dom";
import { DOMAIN } from "@/hooks/useCapabilities";

type StageTag = { color: TagType; label: string; icon?: MaterialNames };

export const PostStageOptions: Record<PostStage, StageTag> = {
  [PostStage.DRAFT]: { color: "information", label: "Draft" },
  [PostStage.IN_REVIEW]: { color: "warn", label: "In Review" },
  [PostStage.PENDING]: { color: "warn", label: "Pending" },
  [PostStage.ACTIVE]: { color: "success", label: "Active" },
  [PostStage.INACTIVE]: { color: "surface", label: "Inactive" },
  [PostStage.REMOVED]: { color: "surface", label: "Removed" },
  [PostStage.UNPOSTED]: { color: "surface", label: "Unposted" },
  [PostStage.ASSIST]: { color: "error", label: "Assist" }
};

export const renderPostStage = (
  stage: string,
  reviewRequestSent?: string,
  tagStyle: "standard" | "strong" = "strong"
) => {
  const data = PostStageOptions[stage as PostStage] || { label: stage, color: "information" as TagType };

  const tagContent = (
    <Tag tagType={data?.color as TagType} tagStyle={tagStyle} testId={data.label}>
      <div className="flex items-center h-[16px]">
        {stage === PostStage.DRAFT && reviewRequestSent && data.icon && (
          <span className="mr-1 mt-[4px]" data-testid="review-icon">
            <Tooltip
              variant="plain"
              color="black"
              supportingText={`Sent for review: ${toDateFormat(reviewRequestSent, { tz: "utc" })}`}
              menuCorner="end-start"
              anchorCorner="start-start"
            >
              <Icon size={16} name={data.icon} />
            </Tooltip>
          </span>
        )}
        {data.label}
      </div>
    </Tag>
  );

  return tagContent;
};

export const formatDate = (dateString: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
};

/**
 * Generates path to job details based on current path context
 */
export const useGetJobDetailsPath = (jobPostId: string) => {
  const location = useLocation();
  if (!jobPostId) return "";
  const basePath = location.pathname.split("/jobs")[0];
  return `${basePath}/jobs/job-post/${jobPostId}/details`;
};

export const createJobPath = (jobPostId: string, subPath?: string, currentPath?: string) => {
  const defaultPath = `/jobs/job-post/${jobPostId}`;
  const basePath = currentPath || defaultPath;
  return subPath ? `${basePath}/${subPath}` : basePath;
};

/**
 * Gets navigation links for job-related pages
 */
export const getJobNavLinks = () => [
  {
    domain: DOMAIN.JOBS,
    label: "Jobs",
    link: { to: "/jobs/all-jobs" },
    icon: "business_center"
  }
];
